import { motion } from "framer-motion";
import { Play, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

// 30-second "Meet the founder" placeholder. Drop a real videoSrc / posterSrc
// via props once the asset is ready — the play button becomes an actual
// <video> control at that point.
export default function FounderVideoIntro({ videoSrc, posterSrc }) {
  const { t } = useTranslation();
  const hasVideo = Boolean(videoSrc);

  return (
    <section
      className="pt-16 md:pt-20 pb-8 md:pb-12 bg-canvas"
      data-testid="founders-video-intro"
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-5">
          <div className="label-eyebrow">{t("foundersVideo.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl leading-[0.95] tracking-tightest">
            {t("foundersVideo.title")}{" "}
            <span className="italic font-serif text-forest/80">
              {t("foundersVideo.italic")}
            </span>
          </h2>
          <p className="mt-6 max-w-md text-ink/70 leading-relaxed">
            {t("foundersVideo.body")}
          </p>
          <div className="mt-6 flex items-center gap-3 text-ink/60 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {t("foundersVideo.duration")}
            </span>
            <span className="w-1 h-1 rounded-full bg-ink/30" />
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-forest">
              Phil-Kholynes Chidi
            </span>
          </div>
        </div>

        {/* Right: 16:9 placeholder card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
          className="lg:col-span-7"
          data-testid="founders-video-card"
        >
          <div className="relative aspect-video rounded-3xl overflow-hidden bg-forest shadow-[0_50px_100px_-40px_rgba(4,26,16,0.55)] ring-1 ring-forest/20">
            {/* Poster / gradient */}
            {posterSrc ? (
              <img
                src={posterSrc}
                alt="Founder video poster"
                className="absolute inset-0 w-full h-full object-cover opacity-60"
              />
            ) : (
              <>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(232,255,0,0.18),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(74,29,150,0.55),transparent_60%)]" />
                <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(4,26,16,0.55)_0%,rgba(4,26,16,0)_60%)]" />
                {/* Grain texture */}
                <div
                  className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
                  }}
                />
              </>
            )}

            {/* Coming-soon pill */}
            {!hasVideo && (
              <div
                className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-canvas/10 backdrop-blur-md border border-canvas/25 text-canvas text-[11px] font-medium tracking-wide"
                data-testid="founders-video-coming-soon"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-lime animate-pulse" />
                {t("foundersVideo.comingSoon")}
              </div>
            )}

            {/* Play button */}
            <button
              type="button"
              disabled={!hasVideo}
              aria-label={t("foundersVideo.playAria")}
              data-testid="founders-video-play"
              className={`absolute inset-0 grid place-items-center group ${
                hasVideo ? "cursor-pointer" : "cursor-not-allowed"
              }`}
            >
              <span
                className={`w-20 h-20 md:w-24 md:h-24 rounded-full grid place-items-center transition-transform duration-500 ${
                  hasVideo
                    ? "bg-lime text-forest group-hover:scale-110"
                    : "bg-canvas/15 backdrop-blur-md border border-canvas/30 text-canvas"
                }`}
              >
                <Play
                  className="w-8 h-8 md:w-10 md:h-10 ml-1"
                  fill="currentColor"
                  strokeWidth={0}
                />
              </span>
            </button>

            {/* Bottom caption */}
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 bg-gradient-to-t from-forest via-forest/70 to-transparent">
              <div className="flex items-end justify-between gap-4 text-canvas">
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-lime">
                    Instantxfer CashApp · Founder note
                  </div>
                  <div className="font-display text-xl md:text-2xl leading-tight mt-1 truncate">
                    {t("foundersVideo.title")}
                  </div>
                </div>
                <span className="shrink-0 font-mono text-xs tracking-[0.18em] text-canvas/70">
                  {t("foundersVideo.duration")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
